import cn from 'classnames'
import { memo, useState } from 'react'

import styles from './FeedbackPostComment.module.scss'

import Button from '~elements/Button'
import { FeedbackPostCommentFragment } from '~generated/operations'
import { formatDate } from '~lib/dateUtils'
import ConfirmModal from '~lib/ui-kit/ConfirmModal'
import Label from '~lib/ui-kit/Label'
import Space from '~lib/ui-kit/Space'
import Text from '~lib/ui-kit/Text'
import useDeleteFeedbackPostComment from '~modules/feedbackTool/usecases/useDeleteFeedbackPostComment'
import { ReplyToOptions } from '~modules/feedbackTool/utils'
import Divider from '~shared/ui/Divider'

interface FeedbackPostCommentProps {
  replyingTo: ReplyToOptions
  comment: FeedbackPostCommentFragment
  replies?: FeedbackPostCommentFragment[]
  subcomment?: never
  disabled?: boolean
  onSetReplyingTo: (v: ReplyToOptions) => void
}

interface FeedbackPostSubcommentProps {
  comment: FeedbackPostCommentFragment
  replyingTo?: never
  replies?: never
  subcomment?: boolean
  disabled?: boolean
  onSetReplyingTo?: never
}

function FeedbackCommentCard({
  comment,
  replies = [],
  replyingTo,
  subcomment,
  disabled,
  onSetReplyingTo,
}: FeedbackPostCommentProps | FeedbackPostSubcommentProps) {
  const [confirmDeleteModalOpened, setConfirmDeleteModalOpened] = useState(false)

  const handleDeleteClick = () => {
    setConfirmDeleteModalOpened(true)
  }

  const handleDeleteCancel = () => {
    setConfirmDeleteModalOpened(false)
  }

  const [deleteFeedbackPostComment, { loading }] = useDeleteFeedbackPostComment()

  const handleSubmitDelete = () => {
    deleteFeedbackPostComment(comment.id)
  }

  const onSetReplyToInfoHandler = () => {
    if (replyingTo?.authorName === comment.author?.name && replyingTo?.commentId === comment.id) {
      onSetReplyingTo(null)
    } else {
      onSetReplyingTo({ commentId: comment.id, authorName: comment.author?.name })
    }
  }

  return (
    <>
      <div className={cn(styles.root, subcomment && styles.subcomment)}>
        <div className={styles.header}>
          <Text type="body-s" variant="secondary" className={styles.date}>
            {formatDate(comment.createdAt, 'day2DigitsMonthShort')}
          </Text>

          {!subcomment && (
            <Button disabled={disabled} variant="plain" onClick={onSetReplyToInfoHandler}>
              Reply
            </Button>
          )}

          {!subcomment && <Divider direction="vertical" className={styles.divider} />}

          <Button disabled={disabled} value={comment.id} variant="highlightText" onClick={handleDeleteClick}>
            Delete
          </Button>
        </div>

        <Space justify="start" align="center" className="mb-2">
          <Text variant="default" type="body-s" bold className="mr-1">
            {comment.author?.name}
          </Text>
          {comment.author?.admin && <Label className={styles.badge}>Employee response</Label>}
        </Space>

        <Text variant="default" type="body-m" className={replies.length > 0 ? 'mb-2' : ''}>
          {comment.text}
        </Text>

        {replies?.map((comment) => (
          <FeedbackCommentCard disabled={disabled} key={comment.id} subcomment comment={comment} />
        ))}
      </div>

      <ConfirmModal
        opened={confirmDeleteModalOpened}
        overlayVariant="blur"
        label="Delete comment?"
        description="Are you sure you want to remove this comment?"
        variant="danger"
        submitText="Delete"
        closeText="Cancel"
        submitLoading={loading}
        onSubmit={handleSubmitDelete}
        onClose={handleDeleteCancel}
        onEscapePress={handleDeleteCancel}
        onOverlayClick={handleDeleteCancel}
      />
    </>
  )
}

export default memo(FeedbackCommentCard)
